import { OperationVariables, TypedDocumentNode, useQuery } from "@apollo/client";
import Add from "@mui/icons-material/Add";
import { Box, Button, Chip, Link, Stack } from "@mui/material";
import { GridRowParams, GridSortModel, GridValidRowModel } from "@mui/x-data-grid-premium";
import { ExportDataButton } from "components/ExportDataButton";
import { WASTE_CONTAINER_TYPES } from "encamp-shared/src/constants/wasteContainerTypes";
import { FeatureFlagKey } from "encamp-shared/src/featureFlags";
import {
  ClockStatus,
  FacilityWasteContainersQuery,
  FacilityWasteContainersQueryVariables,
  TenantWasteContainersQuery,
  TenantWasteContainersQueryVariables,
  WasteClassification,
  WasteContainerListItemFragment,
  WasteContainerStatus,
} from "generated-graphql/graphql";
import { OmnisearchCommandButtonsContext, OmnisearchDataGridV2 } from "hooks/omnisearch-v2";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { FilterKey, OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useTenant } from "hooks/useTenant";
import { useCallback, useMemo, useState } from "react";
import { Link as RouterLink, useNavigate, useSearchParams } from "react-router-dom";

import { prettyPrintWasteClassification } from "../Facilities/Facility/prettyPrintWasteClassification";
import {
  FACILITY_WASTE_CONTAINERS_QUERY,
  TENANT_WASTE_CONTAINERS_QUERY,
  WASTE_CONTAINER_FACILITY_NAME_QUERY,
} from "./api";
import {
  CLOCK_STATUS_LABELS,
  containerTypeLabel,
  CONTAINER_STATUS_LABELS,
  NO_VALUE_PLACEHOLDER,
  formatAmountCapacity,
  formatContainerDate,
} from "./containerDisplay";
import { WasteContainerClockChip } from "./WasteContainerClockChip";
import { WasteContainerDialog } from "./WasteContainerDialog";
import { WasteContainerStatusChip } from "./WasteContainerStatusChip";

type TenantContainerRow = NonNullable<
  NonNullable<TenantWasteContainersQuery["tenant"]>["wasteContainers"]
>["items"][number];

type ContainerRow = WasteContainerListItemFragment & {
  facility?: { id: string; name?: string | null } | null;
};

const CONTAINER_STATUS_LABEL_BY_VALUE: Record<string, string> = Object.fromEntries(
  Object.entries(CONTAINER_STATUS_LABELS)
);
const CONTAINER_TYPE_ENUM_VALUES = WASTE_CONTAINER_TYPES.map((type) => type.code);

function containerStatusFilterLabel(value: string): string {
  return CONTAINER_STATUS_LABEL_BY_VALUE[value] ?? value;
}

const CLOCK_STATUS_LABEL_BY_VALUE: Record<string, string> = Object.fromEntries(
  Object.entries(CLOCK_STATUS_LABELS)
);

function clockStatusFilterLabel(value: string): string {
  return CLOCK_STATUS_LABEL_BY_VALUE[value] ?? value;
}

/**
 * Keyed omnisearch filters for the containers grids (Task 8 backend keys).
 * These are independent of the grid columns: `area`/`wasteStream` filter on a
 * different key than their column's `field` (which drives sort, matching the
 * backend's `accumulationArea`/`wasteStream` sortable names), so declaring
 * them as columns would filter on the wrong key. `facilityId` only applies
 * to the tenant grid, which isn't already scoped to one facility; its
 * `wasteFacility` control always emits a `facilityId:<uuid>` term (see
 * stringifyOmnisearchWithAliases), so the declared key must match that.
 */
function buildAdditionalFilterKeys(includeFacility: boolean): FilterKey[] {
  const keys: FilterKey[] = [
    { key: "area", header: "Area" },
    {
      key: "status",
      header: "Status",
      filterKeyType: "enumAutocomplete",
      enumValues: Object.values(WasteContainerStatus),
      enumPresentationFunction: containerStatusFilterLabel,
    },
    {
      key: "containerType",
      header: "Container Type",
      filterKeyType: "enumAutocomplete",
      enumValues: CONTAINER_TYPE_ENUM_VALUES,
      enumPresentationFunction: containerTypeLabel,
    },
    { key: "wasteStream", header: "Waste Stream" },
    {
      key: "clock",
      header: "Clock / Limit",
      filterKeyType: "enumAutocomplete",
      enumValues: Object.values(ClockStatus),
      enumPresentationFunction: clockStatusFilterLabel,
    },
    {
      key: "classification",
      header: "Classification",
      filterKeyType: "enumAutocomplete",
      enumValues: Object.values(WasteClassification),
      enumPresentationFunction: prettyPrintWasteClassification,
    },
  ];
  if (includeFacility) {
    keys.push({ key: "facilityId", header: "Facility", filterKeyType: "wasteFacility" });
  }
  return keys;
}

/**
 * Column fields match the backend's sortable names (deadlineDate is the
 * persisted min-deadline column the clock's effectiveDeadlineDate mirrors).
 */
function buildColumns<TRow extends ContainerRow>(
  includeFacility: boolean,
  tenantId: string,
  linkWasteStream: boolean
): OmnisearchGridColDef<TRow>[] {
  const columns: OmnisearchGridColDef<TRow>[] = [
    {
      field: "containerLabel",
      headerName: "Label",
      flex: 0.6,
      minWidth: 120,
      sortable: true,
      filterable: false,
    },
    {
      field: "accumulationArea",
      headerName: "Area",
      flex: 0.6,
      minWidth: 120,
      sortable: true,
      filterable: false,
      valueGetter: (_, row) => row.accumulationArea?.name ?? NO_VALUE_PLACEHOLDER,
    },
    {
      field: "wasteStream",
      headerName: "Waste Stream",
      flex: 0.8,
      minWidth: 140,
      sortable: true,
      filterable: false,
      // valueGetter stays so sorting and export keep using the plain name.
      valueGetter: (_, row) => row.wasteStream?.name ?? NO_VALUE_PLACEHOLDER,
      // The link goes to the stream's detail drawer, which is where its waste
      // profile lives. The streams grid filters on shipment lines, so it does
      // not list a stream that has never shipped, and for a stream that exists
      // only for container tracking this is the only route to its profile.
      //
      // Only a link when manifest-views-v2 is on. The streams route is gated on
      // that flag with `fallback="../"`, and this page is gated on
      // container-tracking, so a tenant with one flag and not the other would
      // click the stream and land on the waste overview.
      //
      // stopPropagation keeps the row click from also firing and navigating to
      // the container.
      renderCell: ({ row }) => {
        if (!row.wasteStream) return NO_VALUE_PLACEHOLDER;
        if (!linkWasteStream) return row.wasteStream.name;
        return (
          <Link
            component={RouterLink}
            to={`/o/${tenantId}/waste/streams/${row.wasteStream.id}`}
            onClick={(event) => event.stopPropagation()}
          >
            {row.wasteStream.name}
          </Link>
        );
      },
    },
    {
      field: "amount",
      headerName: "Amount / Capacity",
      flex: 0.6,
      minWidth: 140,
      sortable: true,
      filterable: false,
      headerClassName: "wrapHeader",
      valueGetter: (_, row) => formatAmountCapacity(row),
    },
    {
      field: "status",
      headerName: "Status",
      flex: 0.5,
      minWidth: 130,
      sortable: true,
      filterable: false,
      renderCell: ({ row }) => <WasteContainerStatusChip status={row.status} />,
    },
    {
      field: "deadlineDate",
      headerName: "Deadline",
      flex: 0.5,
      minWidth: 110,
      sortable: true,
      filterable: false,
      valueGetter: (_, row) => formatContainerDate(row.clock.effectiveDeadlineDate),
    },
    {
      field: "clockStatus",
      headerName: "Clock / Limit",
      flex: 0.5,
      minWidth: 120,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => <WasteContainerClockChip status={row.clock.status} />,
    },
    {
      field: "containerType",
      headerName: "Container Type",
      flex: 0.8,
      minWidth: 150,
      sortable: true,
      filterable: false,
      headerClassName: "wrapHeader",
      valueGetter: (_, row) => containerTypeLabel(row.containerType),
    },
  ];
  if (includeFacility) {
    columns.splice(1, 0, {
      field: "facility",
      headerName: "Facility",
      flex: 0.7,
      minWidth: 130,
      sortable: true,
      // Filtering is the omnisearch bar's job on this grid, not the column
      // menu's; `facilityId` is declared as a key above.
      filterable: false,
      valueGetter: (_, row) => row.facility?.name ?? NO_VALUE_PLACEHOLDER,
    });
  }
  return columns;
}

const INITIAL_SORT: GridSortModel = [{ field: "deadlineDate", sort: "asc" }];

const getTenantItems = (data: TenantWasteContainersQuery) =>
  data.tenant?.wasteContainers?.items ?? [];
const getTenantCount = (data: TenantWasteContainersQuery) =>
  data.tenant?.wasteContainers?.count ?? 0;
const getFacilityItems = (data: FacilityWasteContainersQuery) =>
  data.facility?.wasteContainers?.items ?? [];
const getFacilityCount = (data: FacilityWasteContainersQuery) =>
  data.facility?.wasteContainers?.count ?? 0;

const toApiSortModel = (sort: GridSortModel) => sort.map((s) => ({ field: s.field, sort: s.sort }));

type ContainersGridCommandButtons =
  | React.ReactNode[]
  | ((context: OmnisearchCommandButtonsContext) => React.ReactNode[]);

/**
 * The container list rendered for either scope. The tenant and facility grids
 * differ only in their query, data getters, persistence key, empty-row copy,
 * whether the facility column shows, and which id keys the variables — so those
 * are props while every shared grid setting (filters on, initial sort, no row
 * selection) lives here once and can't drift between the two. Saved-view
 * presets come for free from `persistenceKey` + `showFilters` — the popover
 * owns them internally (see Omnisearch.tsx), no extra wiring needed here.
 */
function ContainersGrid<
  TData,
  TVariables extends OperationVariables,
  TRow extends ContainerRow & GridValidRowModel,
>({
  tenantId,
  persistenceKey,
  query,
  getItems,
  getCount,
  buildVariables,
  noRowsMessage,
  includeFacility,
  onRowClick,
  commandButtons,
}: {
  tenantId: string;
  persistenceKey: string;
  query: TypedDocumentNode<TData, TVariables>;
  getItems: (data: TData) => TRow[];
  getCount: (data: TData) => number;
  buildVariables: (
    omnisearch: string,
    pagination: { page: number; pageSize: number },
    sort: GridSortModel
  ) => TVariables;
  noRowsMessage: string;
  includeFacility: boolean;
  onRowClick: (params: GridRowParams<ContainerRow>) => void;
  commandButtons: ContainersGridCommandButtons;
}) {
  const { featureFlags } = useFeatureFlags();
  const linkWasteStream = !!featureFlags[FeatureFlagKey.ManifestViewsV2];
  const columns = useMemo(
    () => buildColumns<TRow>(includeFacility, tenantId, linkWasteStream),
    [includeFacility, tenantId, linkWasteStream]
  );
  const additionalFilterKeys = useMemo(
    () => buildAdditionalFilterKeys(includeFacility),
    [includeFacility]
  );
  return (
    <OmnisearchDataGridV2
      persistenceKey={persistenceKey}
      columns={columns}
      showFilters
      additionalFilterKeys={additionalFilterKeys}
      query={query}
      getItems={getItems}
      getCount={getCount}
      buildVariables={buildVariables}
      noRowsMessage={noRowsMessage}
      initialSortModel={INITIAL_SORT}
      onRowClick={onRowClick}
      disableRowSelectionOnClick
      commandButtons={commandButtons}
      tenantId={tenantId}
    />
  );
}

function TenantContainersGrid({
  tenantId,
  onRowClick,
  commandButtons,
}: {
  tenantId: string;
  onRowClick: (params: GridRowParams<ContainerRow>) => void;
  commandButtons: React.ReactNode[];
}) {
  const buildVariables = useCallback(
    (
      omnisearch: string,
      pagination: { page: number; pageSize: number },
      sort: GridSortModel
    ): TenantWasteContainersQueryVariables => ({
      tenantId,
      search: omnisearch,
      page: pagination.page,
      pageSize: pagination.pageSize,
      sort: toApiSortModel(sort),
    }),
    [tenantId]
  );
  const gridCommandButtons = useCallback(
    ({ search }: OmnisearchCommandButtonsContext) => [
      ...commandButtons,
      <ExportDataButton
        key="export"
        tenantId={tenantId}
        exportType="wasteContainers"
        fileName="tenant-waste-containers"
        filter={{ search }}
        iconOnly
      />,
    ],
    [commandButtons, tenantId]
  );
  return (
    <ContainersGrid<
      TenantWasteContainersQuery,
      TenantWasteContainersQueryVariables,
      TenantContainerRow
    >
      tenantId={tenantId}
      persistenceKey="tenant-waste-containers-table"
      query={TENANT_WASTE_CONTAINERS_QUERY}
      getItems={getTenantItems}
      getCount={getTenantCount}
      buildVariables={buildVariables}
      noRowsMessage="No containers are being tracked for this organization."
      includeFacility
      onRowClick={onRowClick}
      commandButtons={gridCommandButtons}
    />
  );
}

export function FacilityContainersGrid({
  tenantId,
  facilityId,
  onRowClick,
  commandButtons,
  pinnedSearch,
  persistenceKey = "facility-waste-containers-table",
}: {
  tenantId: string;
  facilityId: string;
  onRowClick: (params: GridRowParams<ContainerRow>) => void;
  commandButtons: React.ReactNode[];
  /**
   * A search term ANDed onto every request regardless of the visible
   * omnisearch box — e.g. `area:"<name>"` when this grid is embedded on an
   * area's detail page. The box itself stays empty so the user's own
   * free-text search still narrows further, but can't remove the pin.
   */
  pinnedSearch?: string;
  /**
   * Defaults to the standalone containers page's key. Callers embedding
   * this grid elsewhere (e.g. an area's detail page) should pass a distinct
   * key so persisted column/sort/filter settings don't bleed between the
   * two grids.
   */
  persistenceKey?: string;
}) {
  const buildVariables = useCallback(
    (
      omnisearch: string,
      pagination: { page: number; pageSize: number },
      sort: GridSortModel
    ): FacilityWasteContainersQueryVariables => ({
      facilityId,
      search: [pinnedSearch, omnisearch].filter(Boolean).join(" "),
      page: pagination.page,
      pageSize: pagination.pageSize,
      sort: toApiSortModel(sort),
    }),
    [facilityId, pinnedSearch]
  );
  const gridCommandButtons = useCallback(
    ({ search }: OmnisearchCommandButtonsContext) => [
      ...commandButtons,
      <ExportDataButton
        key="export"
        tenantId={tenantId}
        facilityId={facilityId}
        exportType="wasteContainers"
        fileName="facility-waste-containers"
        filter={{ search }}
        iconOnly
      />,
    ],
    [commandButtons, tenantId, facilityId]
  );
  return (
    <ContainersGrid<
      FacilityWasteContainersQuery,
      FacilityWasteContainersQueryVariables,
      WasteContainerListItemFragment
    >
      tenantId={tenantId}
      persistenceKey={persistenceKey}
      query={FACILITY_WASTE_CONTAINERS_QUERY}
      getItems={getFacilityItems}
      getCount={getFacilityCount}
      buildVariables={buildVariables}
      noRowsMessage="No containers are being tracked at this facility."
      includeFacility={false}
      onRowClick={onRowClick}
      commandButtons={gridCommandButtons}
    />
  );
}

export function WasteContainers() {
  const { tenantId } = useTenant();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const facilityId = searchParams.get("facilityId");
  const [addDialogOpen, setAddDialogOpen] = useState(false);

  useBreadcrumb([
    {
      label: "Waste",
      to: tenantId ? `/o/${tenantId}/waste` : undefined,
    },
    {
      label: "Containers",
    },
  ]);

  const clearFacilityScope = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("facilityId");
    setSearchParams(next, { replace: true });
  };

  const handleRowClick = useCallback(
    (params: GridRowParams<ContainerRow>) => {
      navigate(`/o/${tenantId}/waste/containers/${params.row.id}`);
    },
    [navigate, tenantId]
  );

  const commandButtons = useMemo(
    () => [
      <Button
        key="add-container"
        variant="contained"
        size="small"
        startIcon={<Add />}
        onClick={() => setAddDialogOpen(true)}
      >
        Add container
      </Button>,
    ],
    []
  );

  if (!tenantId) {
    return null;
  }

  return (
    <Box>
      {facilityId && (
        <Stack direction="row" sx={{ pt: 2 }}>
          <FacilityScopeChip facilityId={facilityId} onClear={clearFacilityScope} />
        </Stack>
      )}
      <Box sx={{ py: 3 }}>
        {facilityId ? (
          <FacilityContainersGrid
            tenantId={tenantId}
            facilityId={facilityId}
            onRowClick={handleRowClick}
            commandButtons={commandButtons}
          />
        ) : (
          <TenantContainersGrid
            tenantId={tenantId}
            onRowClick={handleRowClick}
            commandButtons={commandButtons}
          />
        )}
      </Box>
      {addDialogOpen && (
        <WasteContainerDialog
          onClose={() => setAddDialogOpen(false)}
          facilityId={facilityId ?? undefined}
          onCreated={(containerId) => navigate(`/o/${tenantId}/waste/containers/${containerId}`)}
        />
      )}
    </Box>
  );
}

/** Deletable chip showing which facility the list is scoped to. */
function FacilityScopeChip({ facilityId, onClear }: { facilityId: string; onClear: () => void }) {
  // cache-only: the sibling facility-scoped grid query already selects
  // facility.name into the normalized cache, so the chip never needs its own
  // round-trip — it shows the generic label until the grid data lands.
  const { data } = useQuery(WASTE_CONTAINER_FACILITY_NAME_QUERY, {
    fetchPolicy: "cache-only",
    variables: { facilityId },
  });
  const facilityName = data?.facility?.name ?? "Facility";
  return (
    <Chip label={`Facility: ${facilityName}`} size="small" variant="outlined" onDelete={onClear} />
  );
}
